import type { RowExport } from '@/components/ui/table/exportRows'
import {
  ACTIVITY_TYPE_LABELS,
  accountFilterOf,
  activityTypeOf,
  activityViewFor,
} from '../model/activityView'
import type {
  ActivityEntry,
  ActivityQuery,
  ActivityType,
  ActivityView,
} from '../model/types'

export interface ActivityListRow extends ActivityEntry {
  type: ActivityType
  typeLabel: string
}

const rowsByView = new WeakMap<ActivityView, ActivityListRow[]>()

// The view's rows with the Type each one shows. Cached on the view, so the table keeps one data
// identity while the filters stay put.
export const activityListRows = (
  view: ActivityView,
  query: ActivityQuery,
): ActivityListRow[] => {
  const cached = rowsByView.get(view)
  if (cached) return cached
  const inView = accountFilterOf(query.accountIds)
  const rows = view.rows.map((entry) => {
    const type = activityTypeOf(entry, inView)
    return { ...entry, type, typeLabel: ACTIVITY_TYPE_LABELS[type] }
  })
  rowsByView.set(view, rows)
  return rows
}

export const activityRowsFor = (
  entries: ActivityEntry[],
  query: ActivityQuery,
): ActivityListRow[] => activityListRows(activityViewFor(entries, query), query)

// The list has no Symbol column, so the symbol leads the description, as on the board
// ("SOXS DIREXION SHARES ..."). A description that already starts with it is left alone.
export const descriptionWithSymbol = ({
  symbol,
  description,
}: Pick<ActivityEntry, 'symbol' | 'description'>): string => {
  const lead = symbol.trim()
  if (!lead) return description
  const startsWithSymbol =
    description === lead || description.startsWith(`${lead} `)
  return startsWithSymbol ? description : `${lead} ${description}`
}

const OPTION_MULTIPLIER = 100
const SCALE_TOLERANCE = 0.1

// Options print a per-share price on 100-share contracts, bills a price per $100 of face value.
// Worked out from the printed figures, never from the description text.
export const priceScaleNote = (
  entry: Pick<ActivityEntry, 'price' | 'quantity' | 'amount'>,
): string | null => {
  const { price, quantity, amount } = entry
  if (!price || !quantity || !amount) return null
  const ratio = Math.abs(amount) / Math.abs(price * quantity)
  const near = (target: number) =>
    Math.abs(ratio - target) <= target * SCALE_TOLERANCE
  if (near(OPTION_MULTIPLIER)) return '×100'
  if (near(1 / OPTION_MULTIPLIER)) return 'per $100'
  return null
}

const EXPORT_COLUMNS = [
  { key: 'date', label: 'Date' },
  { key: 'settlementDate', label: 'Settles' },
  { key: 'accountName', label: 'Account' },
  { key: 'typeLabel', label: 'Type' },
  { key: 'description', label: 'Description' },
  { key: 'symbol', label: 'Symbol' },
  { key: 'quantity', label: 'Quantity' },
  { key: 'price', label: 'Price' },
  { key: 'amount', label: 'Amount' },
  { key: 'commission', label: 'Commission' },
  { key: 'pairedWith', label: 'Paired with' },
  { key: 'statementFileName', label: 'Statement file' },
  { key: 'sourcePage', label: 'Page' },
]

// Exactly the rows the filters show, with the settle date and file name the table leaves out.
export const activityExport = (rows: ActivityListRow[]): RowExport => ({
  columns: EXPORT_COLUMNS,
  rows: rows.map((row) => ({
    ...row,
    pairedWith: row.pairing
      ? `${row.pairing.partner.accountName}, ${row.pairing.partner.date}`
      : '',
  })),
})
